/**
 * Analytics Utilities for Adobe Analytics
 *
 * Provides helper functions to fire analytics events to Adobe Analytics
 * via the standard data layer (window.dataLayer).
 *
 * IMPORTANT: This implementation matches the Adobe Analytics team's exact specifications.
 * - Uses window.dataLayer (NOT adobeDataLayer)
 * - Event name for AI responses: "aiTireAgentResponse"
 * - Data structure: { event: "aiTireAgentResponse", ai: { responseType: string } }
 * - Dispatches custom event on document.body
 * - All events include page: { pageName } — TRACI's own page name, always injected by pushToDataLayer
 *
 * Do not modify without consulting the Adobe Analytics team.
 */

import { logger } from './logger';
import { armAdobeVar } from '../config/experimentEvents';

declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
  }
}

// TRAC-308: Module-level flag to gate all analytics behind embed mode.
// Only the /embed route sets this to true via setAnalyticsEmbedMode().
let analyticsEnabled = false;

/**
 * Enable or disable analytics event firing.
 * Should be called with `true` only from the /embed route.
 */
export const setAnalyticsEmbedMode = (enabled: boolean): void => {
  analyticsEnabled = enabled;
  logger.debug(`${LOG_PREFIX} Embed mode analytics ${enabled ? 'enabled' : 'disabled'}`);
};

// Track if we've already sent the session ID to Adobe Analytics
// Session ID (eVar92) should only be sent once, not with every event
let sessionIdSent = false;

const LOG_PREFIX = '[Analytics]';

/**
 * Matches the environment marker usePageTracking puts at the front of the title,
 * e.g. "[prod] " or "[pr-1939-my-branch] ".
 */
const ENV_TITLE_PREFIX = /^\[[a-z0-9][a-z0-9-]*\]\s+/i;

/**
 * Get TRACI's current page name from document.title.
 * document.title is kept current by usePageTracking on every route change.
 *
 * TRAC-1065: the title now leads with an environment marker ("[prod] TRACI - Chat")
 * so testers can tell tabs apart. That marker is a browser-tab affordance, not an
 * analytics dimension. Adobe has to keep seeing the same pageName it always has, or
 * prod reporting silently stops lining up with every report from before that change,
 * so the marker is stripped back off here.
 */
const getCurrentPageName = (): string =>
  typeof document !== 'undefined' ? document.title.replace(ENV_TITLE_PREFIX, '') : '';

/**
 * Log detailed analytics error context — only called on failures.
 */
const logAnalyticsError = (action: string, error: unknown): void => {
  const isEmbedded = typeof window !== 'undefined' && window !== window.parent;
  const context = {
    action,
    isEmbedded,
    origin: typeof window !== 'undefined' ? window.location.origin : 'unknown',
    pathname: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    dataLayerExists: typeof window !== 'undefined' && Array.isArray(window.dataLayer),
    dataLayerLength: typeof window !== 'undefined' && Array.isArray(window.dataLayer)
      ? window.dataLayer.length
      : 0,
  };
  logger.error(`${LOG_PREFIX} ${action} failed:`, error);
  logger.error(`${LOG_PREFIX} Context:`, JSON.stringify(context));
};

/**
 * Initialize the data layer if it doesn't exist
 * This must be called before any analytics events are fired
 *
 * Always uses the local window — cross-origin parent access is blocked by browsers.
 */
const initializeDataLayer = (): void => {
  if (typeof window !== 'undefined') {
    try {
      if (!window.dataLayer) {
        window.dataLayer = [];
      }
    } catch (error) {
      logAnalyticsError('initializeDataLayer', error);
    }
  }
};

/**
 * Push an event to the Adobe data layer.
 * Always injects page.pageName (TRACI's own page name) — callers never need to add it.
 * Merges with any existing page fields (e.g., page.name from parent TR.com page).
 */
const pushToDataLayer = (payload: Record<string, unknown>): void => {
  if (typeof window === 'undefined' || !analyticsEnabled) return;

  initializeDataLayer();

  try {
    window.dataLayer = window.dataLayer || [];
    const existingPage = (payload.page as Record<string, unknown>) ?? {};
    window.dataLayer.push({
      ...payload,
      page: { ...existingPage, pageName: getCurrentPageName() },
    });
  } catch (error) {
    logAnalyticsError('pushToDataLayer', error);
  }
};

/**
 * Track when the AI responds with a specific tool/capability
 * Fires: "aiTireAgentResponse" event with responseType
 *
 * This matches the Adobe Analytics team's exact specification:
 * - Uses window.dataLayer (NOT adobeDataLayer)
 * - Event name: "aiTireAgentResponse"
 * - Data structure: { ai: { responseType } }
 * - Dispatches custom event on document.body
 *
 * @param responseType - Type of response (e.g., "tdg_link_provided", "fitment_lookup")
 * @param sessionId - Optional agent session ID to track via eVar92
 * @param pageName - Optional s.pageName from TR.com PDP (e.g., "tr: tires: product details") for prop49
 *
 * Supported responseType values:
 * - "vehicle_priorities_for_tdg" (event132) - When prompting customer for TDG priorities
 * - "tdg_link_provided" (event133) - When providing a TDG link
 * - "fitment_lookup" (event134) - When completing a fitment lookup
 * - "tire_research_kb" (event135) - When performing tire research or knowledge base lookup
 * - "reviews_ratings" (event136) - When retrieving ratings and review data
 * - "compare_tires" (event137) - When comparing tires
 *
 * Session ID (eVar92):
 * - Not available on initial page load
 * - Populated after user sends first message
 * - Sent to Adobe Analytics ONLY ONCE (first analytics event after session creation)
 * - Subsequent events do not include eVar92 (per Adobe best practices)
 */
export const trackAIResponse = (responseType: string, sessionId?: string | null, pageName?: string | null): void => {
  // Build data layer object
  const dataLayerEvent: Record<string, unknown> = {
    event: "aiTireAgentResponse",
    ai: { responseType: responseType },
    ...(pageName && { page: { name: pageName } }),
  };

  // Include session ID in eVar92 ONLY the first time it's available
  // After that, Adobe Analytics will persist it for the session
  if (sessionId && !sessionIdSent) {
    dataLayerEvent.analytics = {
      evars: {
        eVar92: sessionId
      }
    };
    sessionIdSent = true;
    logger.debug(`${LOG_PREFIX} Session ID set in eVar92:`, sessionId.substring(0, 12) + '...');
  }

  pushToDataLayer(dataLayerEvent);

  logger.debug(`${LOG_PREFIX} aiTireAgentResponse:`, responseType, sessionIdSent ? '[session tracked]' : '[no session]');

  // Dispatch custom event on document.body as specified
  if (typeof document !== 'undefined') {
    document.body.dispatchEvent(new CustomEvent("aiTireAgentResponse"));
  }
};

/**
 * TRAC-965 / TRAC-968: the in-frame announcement for every Adobe moment.
 *
 * The Adobe tag that runs INSIDE the chat frame (the "Retail" property, loaded via
 * VITE_ADOBE_LAUNCH_URL) is the route proven to reach Adobe from production: its rules read
 * `window.dataLayer` and listen for a DOM custom event on `document.body`, then send a
 * custom-link beacon to Tire Rack's report suite with eVar92 = session id. The route via
 * the host page (`hostAnalytics.ts` → postMessage → web team's `linkTracking`) needs a
 * branch on tirerack.com that does not exist, so every moment is ALSO announced here.
 *
 * Since 2026-09-04 the chat also SENDS the custom link itself (see `sendAdobeLink` below), so no
 * tag rule is required. The announcement below is kept as the readable record of each moment
 * (and the contract, should a tag rule ever replace the direct call):
 *   Event:  custom event `aiTireAgentEvent` on body (like the existing `aiTireAgentResponse`)
 *   Data:   the newest `window.dataLayer` entry with `event === "aiTireAgentEvent"`:
 *             { event: "aiTireAgentEvent",
 *               ai: { linkName, event, ...extra },          // linkName is what to send
 *               analytics: { evars: { eVar92: sessionId } }, // same key the existing rules read
 *               page: { name, pageName } }                   // TR page name when known
 *   Action: set eVar92 / prop49 as the existing rules do, send beacon type link,
 *           linkName = ai.linkName.
 */
export const AI_EVENT_NAME = "aiTireAgentEvent";

// The tirerack.com page name is owned by the chat service; the embed route registers a
// provider so this module stays free of service imports (and unit-testable).
let pageNameProvider: (() => string | null | undefined) | null = null;

export const setAnalyticsPageNameProvider = (
  provider: (() => string | null | undefined) | null,
): void => {
  pageNameProvider = provider;
};

/**
 * Announce one Adobe moment inside the frame. Returns the data-layer entry that was pushed,
 * or null when analytics is off (standalone app, health-check canary) or not in a browser.
 * Never throws — measurement never breaks the thing it measures.
 */
export const trackAIEvent = (
  linkName: string,
  event: string,
  sessionId?: string | null,
  extra?: Record<string, unknown>,
): Record<string, unknown> | null => {
  if (typeof window === "undefined" || !analyticsEnabled) return null;
  try {
    let pageName: string | null | undefined = null;
    try { pageName = pageNameProvider?.(); } catch { pageName = null; }
    // TRAC-1108: the experiment arm gets its own eVar only once web's analytics team
    // allocates one (EXPERIMENT_ARM_ADOBE_EVAR, unset today). Until then armVar is null
    // and the entry and the link are exactly what they were.
    const armVar = armAdobeVar(extra?.experimentId, extra?.arm);
    const evars: Record<string, string> = {
      ...(sessionId && { eVar92: sessionId }),
      ...(armVar && { [armVar.name]: armVar.value }),
    };
    const entry: Record<string, unknown> = {
      event: AI_EVENT_NAME,
      ai: { ...(extra ?? {}), linkName, event },
      ...(Object.keys(evars).length > 0 && { analytics: { evars } }),
      ...(pageName && { page: { name: pageName } }),
    };
    pushToDataLayer(entry);
    logger.debug(`${LOG_PREFIX} ${AI_EVENT_NAME}:`, linkName, sessionId ? "[session]" : "[no session]");
    if (typeof document !== "undefined" && document.body) {
      document.body.dispatchEvent(
        new CustomEvent(AI_EVENT_NAME, { detail: { linkName, event, sessionId: sessionId ?? null } }),
      );
    }
    sendAdobeLink(linkName, sessionId ?? null, pageName ?? null, 0, armVar);
    return entry;
  } catch (error) {
    logAnalyticsError("trackAIEvent", error);
    return null;
  }
};

// ─── the custom-link call itself ─────────────────────────────────────────────────────
//
// 2026-09-04: the analytics team confirmed a custom link needs nothing configured on the
// Adobe side, and that nobody on their side edits the tag rules for us. So the chat sends
// the custom link call itself, the way tirerack.com's own `linkTracking` does: through the
// AppMeasurement tracker that Tire Rack's tag has already created inside this frame. No
// report suite id, tracking server or visitor config is hard-coded here — we reuse the
// instance the tag built (AppMeasurement registers every instance in `window.s_c_il` with
// `_c === "s_c"`). If the tag is absent or still loading, we retry briefly, then give up
// quietly. Whoever adds a tag rule for `aiTireAgentEvent` later must first remove this,
// or every link counts twice.

/** Minimal shape of an AppMeasurement tracker instance (AppMeasurement 2.x). */
interface AdobeTracker {
  _c?: string;
  account?: string;
  linkTrackVars?: string;
  linkTrackEvents?: string;
  eVar92?: string;
  prop49?: string;
  /** Any other eVar, set only for the experiment arm once one is allocated (TRAC-1108). */
  [eVar: `eVar${number}`]: string | undefined;
  tl: (linkObject: boolean | HTMLElement, linkType: string, linkName: string) => void;
}

const ADOBE_LINK_VARS = "eVar92,prop49";
const TRACKER_RETRY_MS = 500;
const TRACKER_RETRY_LIMIT = 20; // ~10s: the tag loads async and may land after our first moment

/** The AppMeasurement instance the Adobe tag created in this window, if any. */
export const findAdobeTracker = (): AdobeTracker | null => {
  if (typeof window === "undefined") return null;
  const list = (window as unknown as { s_c_il?: unknown[] }).s_c_il;
  if (!Array.isArray(list)) return null;
  for (const inst of list) {
    const t = inst as Partial<AdobeTracker> | null;
    if (t && t._c === "s_c" && typeof t.tl === "function") return t as AdobeTracker;
  }
  return null;
};

/**
 * Send one Adobe custom link ("link_o") through the tag's tracker, with the session id in
 * eVar92 and the tirerack.com page name in prop49, plus the experiment arm's eVar when one
 * is configured (`armVar`, TRAC-1108; null today). Retries while the tag is still loading.
 * Returns a promise resolving to true when the call was made, false when it never could be.
 * Never throws — measurement never breaks the thing it measures.
 */
export const sendAdobeLink = (
  linkName: string,
  sessionId: string | null,
  pageName: string | null,
  attempt = 0,
  armVar: { name: `eVar${number}`; value: string } | null = null,
): Promise<boolean> =>
  new Promise((resolve) => {
    try {
      const tracker = findAdobeTracker();
      if (!tracker) {
        if (attempt >= TRACKER_RETRY_LIMIT) {
          logger.warn(`${LOG_PREFIX} Adobe tracker never appeared — link not sent:`, linkName);
          resolve(false);
          return;
        }
        setTimeout(() => { sendAdobeLink(linkName, sessionId, pageName, attempt + 1, armVar).then(resolve); }, TRACKER_RETRY_MS);
        return;
      }
      tracker.linkTrackVars = armVar ? `${ADOBE_LINK_VARS},${armVar.name}` : ADOBE_LINK_VARS;
      tracker.linkTrackEvents = "None";
      tracker.eVar92 = sessionId ?? "";
      if (armVar) tracker[armVar.name] = armVar.value;
      tracker.prop49 = pageName ?? getCurrentPageName();
      tracker.tl(true, "o", linkName);
      logger.debug(`${LOG_PREFIX} Adobe custom link sent:`, linkName);
      resolve(true);
    } catch (error) {
      logAnalyticsError("sendAdobeLink", error);
      resolve(false);
    }
  });

/**
 * Track when the AI chat is opened
 * Fires: "tr: ai tire agent: open"
 * TRAC-373: Include TR.com s.pageName when available
 */
export const trackAIOpen = (pageName?: string | null): void => {
  pushToDataLayer({
    event: "tr: ai tire agent: open",
    ...(pageName && { page: { name: pageName } }),
  });
  logger.debug(`${LOG_PREFIX} tr: ai tire agent: open`, pageName ? `[page: ${pageName}]` : '[no page]');
};

/**
 * Track when the AI chat is closed
 * Fires: "tr: ai tire agent: close"
 * TRAC-373: Include TR.com s.pageName when available
 */
export const trackAIClose = (pageName?: string | null): void => {
  pushToDataLayer({
    event: "tr: ai tire agent: close",
    ...(pageName && { page: { name: pageName } }),
  });
  logger.debug(`${LOG_PREFIX} tr: ai tire agent: close`, pageName ? `[page: ${pageName}]` : '[no page]');
};

/**
 * Add analytics tracking parameter to a URL
 * Appends oEvent=event131 to track link clicks from AI responses
 *
 * @param url - The URL to add the parameter to
 * @returns URL with oEvent=event131 appended
 */
export const addAnalyticsParam = (url: string): string => {
  if (!url || !analyticsEnabled) return url;

  // Clean markdown characters from the end of URL
  // These can appear if URL is inside markdown formatting
  let cleanUrl = url
    .replace(/\*+$/g, '')   // Remove trailing * or ** (markdown bold/italic)
    .replace(/_+$/g, '')    // Remove trailing _ or __ (markdown italic/bold)
    .trim();

  try {
    const urlObj = new URL(cleanUrl);
    urlObj.searchParams.set('oEvent', 'event131');
    return urlObj.toString();
  } catch (e) {
    // If URL parsing fails, fall back to simple string append
    const separator = cleanUrl.includes('?') ? '&' : '?';
    return `${cleanUrl}${separator}oEvent=event131`;
  }
};
